/**
 * Hotcoin timeframe configuration and interval mapping.
 * REST API uses step parameter in seconds: 60, 300, 900, 1800, 3600, 86400, 604800, 2592000.
 * WebSocket uses period format: 1m, 5m, 15m, 30m, 1h, 2h, 4h, 6h, 12h, 1d, 3d, 5d, 1w, 1mo.
 * @see https://www.hotcoin.com/en_US/docs/#get-kline-data
 * @see https://www.hotcoin.com/en_US/docs/#kline-data (WebSocket)
 */

import {
  registerExchangeTimeframes,
  resolutionToSeconds,
  type TimeframeConfig,
} from '../../lib/timeframes';

/**
 * Hotcoin supported kline intervals.
 */
export const HOTCOIN_TIMEFRAMES: TimeframeConfig = {
  native: ['1m', '5m', '15m', '30m', '1h', '2h', '4h', '6h', '12h', '1d', '3d', '5d', '1w', '1mo'],
  synthetic: ['1s'], // Use ticker for 1s
  minIntervalSeconds: 60,
};

// Register with central registry
registerExchangeTimeframes('hotcoin', HOTCOIN_TIMEFRAMES);

/**
 * Maps TradingView resolution to Hotcoin REST API interval format (in seconds).
 */
export const mapToHotcoinInterval = (resolution: string): number => {
  const seconds = resolutionToSeconds(resolution);

  // Exact matches for all supported intervals
  if (seconds === 60) return 60; // 1 minute
  if (seconds === 300) return 300; // 5 minutes
  if (seconds === 900) return 900; // 15 minutes
  if (seconds === 1800) return 1800; // 30 minutes
  if (seconds === 3600) return 3600; // 1 hour
  if (seconds === 86400) return 86400; // 1 day
  if (seconds === 604800) return 604800; // 1 week
  if (seconds === 2592000) return 2592000; // 1 month (30 days)

  // Default to closest supported interval (round down)
  if (seconds < 60) return 60;
  if (seconds < 300) return 60;
  if (seconds < 900) return 300;
  if (seconds < 1800) return 900;
  if (seconds < 3600) return 1800;
  if (seconds < 86400) return 3600;
  if (seconds < 604800) return 86400;
  if (seconds < 2592000) return 604800;

  return 2592000;
};

/**
 * Maps TradingView resolution to Hotcoin WebSocket interval format.
 * NOTE: This differs from REST API which uses seconds (60, 300, etc.).
 */
export const mapToHotcoinWsInterval = (resolution: string): string => {
  const seconds = resolutionToSeconds(resolution);

  // Exact matches for all supported intervals
  if (seconds === 60) return '1m';
  if (seconds === 300) return '5m';
  if (seconds === 900) return '15m';
  if (seconds === 1800) return '30m';
  if (seconds === 3600) return '1h';
  if (seconds === 7200) return '2h';
  if (seconds === 14400) return '4h';
  if (seconds === 21600) return '6h';
  if (seconds === 43200) return '12h';
  if (seconds === 86400) return '1d';
  if (seconds === 259200) return '3d';
  if (seconds === 432000) return '5d';
  if (seconds === 604800) return '1w';
  if (seconds === 2592000) return '1mo';

  // Default to closest supported interval (round down)
  if (seconds < 60) return '1m';
  if (seconds < 300) return '1m';
  if (seconds < 900) return '5m';
  if (seconds < 1800) return '15m';
  if (seconds < 3600) return '30m';
  if (seconds < 7200) return '1h';
  if (seconds < 14400) return '2h';
  if (seconds < 21600) return '4h';
  if (seconds < 43200) return '6h';
  if (seconds < 86400) return '12h';
  if (seconds < 259200) return '1d';
  if (seconds < 432000) return '3d';
  if (seconds < 604800) return '5d';
  if (seconds < 2592000) return '1w';

  return '1mo';
};
